import type { ChargeLayerAudit } from "@/pages/sy/models/ChargeLayerAudit";

export interface ProcessedLayer {
  key?: number | null;
  chargeLayerId?: number;
  layerNumber?: number | null;
  recipeLayerId?: number | null;
  commodityName: string;
  weight: number;
  color: string;
  isManualEdit: boolean;
}

const nullsLast = (value: number | null | undefined) =>
  value ?? Number.MAX_SAFE_INTEGER;

const compareByRecipeOrder = (a: ProcessedLayer, b: ProcessedLayer) =>
  nullsLast(a.recipeLayerId) - nullsLast(b.recipeLayerId) ||
  nullsLast(a.chargeLayerId) - nullsLast(b.chargeLayerId);

export const getMultiCommodityLayerNumbers = (
  audits: ChargeLayerAudit[],
): Set<number> => {
  const materialsByLayer = new Map<number, Set<number>>();
  for (const audit of audits) {
    if (audit.LayerNumber == null) continue;
    const materials = materialsByLayer.get(audit.LayerNumber) ?? new Set();
    materials.add(audit.ChargeLayerId);
    materialsByLayer.set(audit.LayerNumber, materials);
  }
  return new Set(
    Array.from(materialsByLayer)
      .filter(([, materials]) => materials.size > 1)
      .map(([layerNumber]) => layerNumber),
  );
};

const mergeLayerMaterials = (blocks: ProcessedLayer[]): ProcessedLayer[] => {
  const materials = new Map<number | undefined, ProcessedLayer>();
  for (const block of blocks) {
    const material = materials.get(block.chargeLayerId);
    if (material) {
      material.weight += block.weight;
      material.isManualEdit = material.isManualEdit || block.isManualEdit;
    } else {
      materials.set(block.chargeLayerId, { ...block });
    }
  }
  return Array.from(materials.values()).sort(compareByRecipeOrder);
};

export type LayerFrame = {
  start: number;
  end: number;
};

type LayerRun = LayerFrame & {
  layerNumber: number | null | undefined;
  materials: Set<number | null | undefined>;
};

export const getMultiCommodityFrames = (
  blocks: ProcessedLayer[],
): LayerFrame[] => {
  const runs: LayerRun[] = [];
  let cumulativeWeight = 0;
  for (const block of blocks) {
    const lastRun = runs[runs.length - 1];
    const material = block.chargeLayerId ?? block.key;
    if (
      lastRun &&
      block.layerNumber != null &&
      lastRun.layerNumber === block.layerNumber
    ) {
      lastRun.end += block.weight;
      lastRun.materials.add(material);
    } else {
      runs.push({
        layerNumber: block.layerNumber,
        start: cumulativeWeight,
        end: cumulativeWeight + block.weight,
        materials: new Set([material]),
      });
    }
    cumulativeWeight += block.weight;
  }
  return runs
    .filter((run) => run.materials.size > 1)
    .map(({ start, end }) => ({ start, end }));
};

export const groupMultiCommodityLayers = (
  sequenceLayers: ProcessedLayer[],
  multiCommodityLayerNumbers: Set<number>,
): ProcessedLayer[] => {
  const mergedLayerNumbers = new Set<number>();
  return sequenceLayers.flatMap((block) => {
    const { layerNumber } = block;
    if (layerNumber == null || !multiCommodityLayerNumbers.has(layerNumber)) {
      return [block];
    }
    if (mergedLayerNumbers.has(layerNumber)) return [];
    mergedLayerNumbers.add(layerNumber);
    return mergeLayerMaterials(
      sequenceLayers.filter((other) => other.layerNumber === layerNumber),
    );
  });
};
